// useEvaluation.ts
// Handles all paper evaluation API calls.
// Encapsulates loading state, error handling, and batch processing
// so components only need to call evaluate() and react to state changes.
//
// Batch evaluation now hits /evaluate/batch which runs papers in parallel
// on the backend — no more sequential client-side loop for batches.

import { useState, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import { API_BASE, EVALUATION_TIMEOUT_MS } from '../lib/constants';
import type { EvaluationResult, BatchResult, Priority } from '../types';

interface UseEvaluationResult {
  result: EvaluationResult | null;
  batchResult: BatchResult | null;
  loading: boolean;
  batchProgress: number;
  error: string | null;
  evaluate: (files: File[], priority: Priority) => Promise<void>;
  reset: () => void;
}

/** Converts axios errors into user-friendly messages */
function parseError(err: unknown): string {
  if (err instanceof AxiosError) {
    if (err.code === 'ECONNABORTED') return 'Request timed out — the paper may be too large.';
    if (err.response?.status === 429) return 'Rate limited — please wait a moment and try again.';
    if (err.response?.status === 401) return 'Invalid API key — please check your Anthropic key.';
    if (err.response?.status === 400) return err.response.data?.detail || 'Could not process this PDF.';
    return 'Something went wrong. Please try again.';
  }
  return 'An unexpected error occurred.';
}

/** Evaluates a single PDF file */
async function evaluateSingle(
  file: File,
  priority: Priority
): Promise<EvaluationResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('priority', priority);

  const res = await axios.post(`${API_BASE}/evaluate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: EVALUATION_TIMEOUT_MS,
  });

  return res.data;
}

/**
 * Evaluates multiple PDFs in a single request.
 * The backend runs evaluations in parallel — much faster than sequential.
 */
async function evaluateBatch(
  files: File[],
  priority: Priority,
): Promise<BatchResult> {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  formData.append('priority', priority);

  // Timeout scales with batch size — each paper needs headroom
  const timeout = EVALUATION_TIMEOUT_MS * Math.max(1, Math.ceil(files.length / 3));

  const res = await axios.post(`${API_BASE}/evaluate/batch`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout,
  });

  return {
    results: res.data.results,
    total: res.data.total,
  };
}

export function useEvaluation(): UseEvaluationResult {
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const evaluate = useCallback(async (
    files: File[],
    priority: Priority,
  ) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setBatchResult(null);
    setBatchProgress(0);

    try {
      if (files.length === 1) {
        const data = await evaluateSingle(files[0], priority);
        setResult(data);
      } else {
        // All files go to the parallel batch endpoint
        const data = await evaluateBatch(files, priority);
        setBatchResult(data);
      }
    } catch (err) {
      setError(parseError(err));
    } finally {
      setBatchProgress(-1);
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setBatchResult(null);
    setError(null);
    setBatchProgress(-1);
    setLoading(false);
  }, []);

  return {
    result,
    batchResult,
    loading,
    batchProgress,
    error,
    evaluate,
    reset,
  };
}